import * as React from 'react'
import { stripHtml } from 'string-strip-html'

import { getParameterByName, SMALL_ICON_BASE_SRC } from '../../../Shared'
import { KaraokeChunk } from '../../../Shared/components/readAloud/readAloudKaraoke'
import useChunkedReadAloud from '../../../Shared/hooks/readAloud/useChunkedReadAloud'
import useModalAccessibility from '../../../Shared/hooks/useModalAccessibility'
import { collectHtmlWords } from '../../../Shared/libs/speech/speechText'
import {
  MULTIPLE_UNNECESSARY_ADDITION,
  MULTIPLE_UNNECESSARY_DELETION,
  SINGLE_UNNECESSARY_ADDITION,
  SINGLE_UNNECESSARY_DELETION,
  UNNECESSARY_CHANGE,
  UNNECESSARY_SPACE
} from '../../helpers/determineUnnecessaryEditType'

const notNecessaryIconSrc = `${SMALL_ICON_BASE_SRC}/review-not-necessary.svg`
const incorrectIconSrc = `${SMALL_ICON_BASE_SRC}/review-incorrect.svg`
const correctIconSrc = `${SMALL_ICON_BASE_SRC}/review-correct.svg`

export const UNNECESSARY_EXPLANATIONS = {
  [UNNECESSARY_SPACE]: 'You added an unnecessary space.',
  [MULTIPLE_UNNECESSARY_DELETION]: "You took out words that weren't part of an error.",
  [SINGLE_UNNECESSARY_DELETION]: "You took out a word that wasn't part of an error.",
  [MULTIPLE_UNNECESSARY_ADDITION]: 'You added unnecessary words.',
  [SINGLE_UNNECESSARY_ADDITION]: 'You added an unnecessary word.',
  [UNNECESSARY_CHANGE]: 'You made an unnecessary change.',
}

const HEADERS = {
  correct: { src: correctIconSrc, text: 'Correct', },
  incorrect: { src: incorrectIconSrc, text: 'Incorrect', },
  notNecessary: { src: notNecessaryIconSrc, text: 'Not necessary', },
}

const headerFor = (state) => HEADERS[state] || (UNNECESSARY_EXPLANATIONS[state] ? HEADERS.notNecessary : { src: undefined, text: undefined, })

const renderConceptExplanation = (concept, spokenWordIndex, readAloudEnabled) => {
  if (!(concept && concept.explanation)) { return <span /> }
  return (
    <div className="explanation">
      <p className="label">Explanation</p>
      {readAloudEnabled
        ? <KaraokeChunk element="div" html={concept.explanation} spokenWordIndex={spokenWordIndex} />
        : <p dangerouslySetInnerHTML={{ __html: concept.explanation }} />}
    </div>
  )
}

const renderCorrectAnswers = (displayText) => {
  if (!displayText) { return }

  const correctAnswerArray = displayText ? displayText.split('~').map(s => s.trim()).filter(Boolean) : []
  const correctAnswers = correctAnswerArray.map(ca => <span className="correct-answer" key={ca}>{ca}</span>)
  const correctAnswerHTML = <p className="correct-answers">{correctAnswers}</p>
  return (
    <div aria-hidden={true}>
      <p className="label">Correct</p>
      {correctAnswerHTML}
    </div>
  )
}

const renderNotNecessaryExplanation = (explanation, spokenWordIndex, readAloudEnabled) => {
  if (!explanation) { return }
  if (readAloudEnabled) { return <KaraokeChunk className="unnecessary-explanation" html={explanation} spokenWordIndex={spokenWordIndex} /> }

  return <p className="unnecessary-explanation">{explanation}</p>
}

const FEEDBACK_LABEL = 'Read the feedback aloud'
const statusHtml = (headerText, explanation) => `<h2>${headerText}</h2>${explanation ? `<p>${explanation}</p>` : ''}`

const EditTooltip = ({ state, id, tooltipHeight, back, numberOfEdits, next, index, displayText, concept, incorrectText, sentenceContext, }) => {
  const { modalRef, } = useModalAccessibility(next) // there should not be a way to entirely escape this modal
  const previewSessionCompleted = !!(getParameterByName('anonymous', window.location.href) && index + 1 === numberOfEdits)

  const { src, text: headerText, } = headerFor(state)
  const unnecessaryExplanation = UNNECESSARY_EXPLANATIONS[state]
  const conceptExplanation = concept && concept.explanation
  const chunks = React.useMemo(() => {
    if (!headerText) { return [] }

    return [{ html: statusHtml(headerText, unnecessaryExplanation), }, ...(conceptExplanation ? [{ html: conceptExplanation, }] : [])]
  }, [headerText, unnecessaryExplanation, conceptExplanation])
  const { activeChunkIndex, activeWordIndex, readAloudEnabled, speaker, } = useChunkedReadAloud({ chunks, label: FEEDBACK_LABEL, })
  const headerWordCount = readAloudEnabled && headerText ? collectHtmlWords(headerText).length : 0
  const statusWordIndex = activeChunkIndex === 0 ? activeWordIndex : null
  const headerWordIndex = statusWordIndex !== null && statusWordIndex < headerWordCount ? statusWordIndex : null
  const lineWordIndex = statusWordIndex !== null && statusWordIndex >= headerWordCount ? statusWordIndex - headerWordCount : null
  const explanationWordIndex = activeChunkIndex === 1 ? activeWordIndex : null
  const parentElement = document.getElementById(id)
  const style = parentElement ? { top: `${parentElement.offsetTop + 5}px`, height: tooltipHeight } : {}
  const backButton = back ? <button className="quill-button medium secondary outlined focus-on-light" onClick={back} type="button">Back</button> : <div className="placeholder" />
  const nextButtonClassName = `quill-button medium primary contained focus-on-light${previewSessionCompleted ? ' disabled' : ''}`
  const nextButton = (
    <button
      className={nextButtonClassName}
      disabled={previewSessionCompleted}
      onClick={next}
      type="button"
    >
      {index + 1 === numberOfEdits ? 'Done' : 'Next'}
    </button>
  )
  return (
    <div
      aria-labelledby='title'
      aria-live="polite"
      aria-modal={true}
      className="edit-tooltip"
      ref={modalRef}
      role="dialog"
      style={style}
      tabIndex={-1}
    >
      <div className="top-section">
        <img alt="" src={src} />
        {readAloudEnabled ? <KaraokeChunk element="h2" html={headerText} id="title" spokenWordIndex={headerWordIndex} /> : <h2 id="title">{headerText}</h2>}
        {speaker}
      </div>
      <div className="middle-section">
        {renderNotNecessaryExplanation(unnecessaryExplanation, lineWordIndex, readAloudEnabled)}
        <p className="sr-only">
          {sentenceContext && `Full sentence: ${stripHtml(sentenceContext).result} `}
          The correct text was {displayText}.
          {incorrectText && ` You submitted ${incorrectText === ' ' ? 'an empty space' : incorrectText}.`}
        </p>
        {renderCorrectAnswers(displayText)}
        {renderConceptExplanation(concept, explanationWordIndex, readAloudEnabled)}
      </div>
      <div className="button-section">
        {backButton}
        <div className="counter">{index + 1} of {numberOfEdits}</div>
        {nextButton}
      </div>
    </div>
  )

}

export default EditTooltip
